import React, { useEffect, useState } from 'react';

import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import { Modal } from '@bringg/react-components';
import { Notification } from '@bringg/react-components';
import { RunStatus } from '@bringg/dashboard-sdk/dist/Run/runs.consts';
import { Run, TaskStatus, ApplicationUuid } from '@bringg/types';

import UserAssigner from './user-assigner/user-assigner';
import RunEditor from '../run-editor';
import useStores from '../../../recipes/use-stores';
import getTeamId from '../run-assigner-team-id-provider';
import { getWaypointRange } from '../../../services/tasks/task-time-run-provider';
import plannedRunsProvider from '../../../services/runs/planned-runs-provider';
import { Spinner } from '../../../components';
import { TasksMassAssignErrorCode } from '../../../stores/tasks/tasks-error-codes';
import {
	assignErrorMessage,
	assignSuccessMessage,
	CloseDataType,
	getCloseData,
	UserAssignmentModalParams,
	ViewNames
} from './consts';
import UserAssignerFooter from './user-assigner-footer/user-assigner-footer';
import OptimizationView from './optimization-view/optimization-view';
import useUserAssignerStore from './use-user-assigner-store';
import { userAssignerRootStore } from './user-assigner-store';

import './user-assigner-modal.scss';

export interface UserAssignerModalProps {
	onClose: (data?: CloseDataType) => void;
	modalParams: UserAssignmentModalParams;
	startRealtimeAsync?: () => { onActionSuccess: (results: any) => void; onActionFail: () => void };
}

const UserAssignerModal = ({ onClose, modalParams, startRealtimeAsync }: UserAssignerModalProps) => {
	const { t } = useTranslation();

	const {
		vehicleTypesStore,
		vehiclesStore,
		teamsStore,
		plannedRoutesStore,
		runStore,
		applicationMerchantConfigurationStore
	} = useStores();

	const {
		currentView,
		selectedDriver,
		selectedRun,
		setRunParams,
		unstartedRunTasks,
		setUnstartedRunTasks,
		teamPlannedRoutes,
		setTeamPlannedRoutes,
		setRunsById,
		setError,
		optimizationResult
	} = useUserAssignerStore();

	const [dateRange, setDateRange] = useState<{ startTime: Date; endTime: Date }>(null);
	const [isReady, setIsReady] = useState(false);

	const titleByView: Record<ViewNames, string> = {
		table: 'RUN_USER_ASSIGNMENT.FIND_DRIVER_TITLE',
		newRoute: 'RUN_USER_ASSIGNMENT.NEW_ROUTE_TITLE',
		optimizationView: 'RUN_USER_ASSIGNMENT.OPTIMIZE_ROUTE_TITLE'
	};

	const taskStartedStatuses = [TaskStatus.OnTheWay, TaskStatus.CheckedIn, TaskStatus.Done, TaskStatus.Cancelled];

	const fetchData = async (tasksDateRange: { endTime: Date; startTime: Date }) => {
		const query = modalParams.data.tasksRunIds
			? {
					ids: modalParams.data.tasksRunIds,
					run_status: RunStatus.OPEN
			  }
			: {
					ready_to_execute: modalParams.readyToExecute,
					is_planned: true,
					run_status: RunStatus.OPEN
			  };

		await Promise.all([
			applicationMerchantConfigurationStore.fetchConfiguration(ApplicationUuid.RouteOptimizer2),
			teamsStore.fetchAll(),
			vehicleTypesStore.fetchAll(),
			vehiclesStore.loadAllByTeam(getTeamId(modalParams.source)),
			runStore
				.loadMany(query)
				.then(async (runs: Run[]) => {
					if (
						modalParams.data.isSavedRun &&
						modalParams.source.run &&
						!runStore.get(modalParams.source.run.id)
					) {
						return runStore.load(modalParams.source.run.id).then(run => {
							return [...runs, run];
						});
					}
					return runs.length ? runs : modalParams.source.run ? [modalParams.source.run] : [];
				})
				.then(runs => {
					setRunsById(
						plannedRunsProvider.getPlannedRunsOfSameDayById(
							runs,
							tasksDateRange,
							modalParams.source.run?.id,
							teamsStore.get(getTeamId(modalParams.source))?.time_zone
						)
					);
				}),
			plannedRoutesStore.getAllByTeam(getTeamId(modalParams.source)).then(setTeamPlannedRoutes)
		]);
		setIsReady(true);
	};

	useEffect(() => {
		let tasksDateRange;

		if (modalParams.source.run) {
			const runTasks = modalParams.source.runTasks.filter(
				task => task && !taskStartedStatuses.includes(task.status)
			);

			setUnstartedRunTasks(runTasks);
			tasksDateRange = getWaypointRange(runTasks);
		} else {
			tasksDateRange = getWaypointRange(modalParams.source.tasks);
		}

		setDateRange(tasksDateRange);

		// pay attention this might throw in some edge cases
		fetchData(tasksDateRange);

		return () => {
			userAssignerRootStore.clearStore();
		};
	}, []);

	const assignToRun = async targetRun => {
		const driverId = selectedDriver ? selectedDriver.id : selectedRun.user_id;
		if (modalParams.data.doNotNotifyServerOnAssign) {
			const data = getCloseData(targetRun.id, modalParams.data.drivers, driverId);

			return onClose(data);
		}
		return callServer(
			assignSuccessMessage,
			assignErrorMessage,
			async tasks => {
				return getRootEnv().dashboardSdk.sdk.v2.tasks.massAssign(
					driverId,
					tasks.map(task => task.id),
					{},
					targetRun
				);
			},
			getCloseData(targetRun.id, modalParams.data.drivers, driverId)
		);
	};

	const unassignTasks = async (unassignedOrder: boolean) => {
		return callServer('RUN_USER_ASSIGNMENT.UNASSIGN_SUCCESS', 'RUN_USER_ASSIGNMENT.UNASSIGN_ERROR', async tasks => {
			const taskIds = tasks.map(task => task.id);
			const options = { unassign_tasks_from_run: unassignedOrder, ignore_no_user_assigned: true };
			return getRootEnv().dashboardSdk.sdk.v2.tasks.massUnassign(taskIds, options);
		});
	};

	const callServer = async (successMessage, errorMessage, actionFunc, extraData?) => {
		let onActionFail, onActionSuccess;

		try {
			const tasks = modalParams.source.tasks || unstartedRunTasks || [];

			if (typeof startRealtimeAsync === 'function') {
				({ onActionSuccess, onActionFail } = startRealtimeAsync());
			}

			const { success, result } = await actionFunc(tasks);

			const data = extraData || {};

			if (success) {
				Notification.success(t(successMessage));
				onActionSuccess?.(result);
				onClose(data);
			} else {
				Notification.error(t(errorMessage));
				onActionFail?.();
			}
		} catch (e) {
			console.error(`failed`, e);
			Notification.error(t(errorMessage));
			onActionFail?.();

			// @ts-ignore
			if (e.rc === TasksMassAssignErrorCode.RunExternalIdExists) {
				setError('RUN_USER_ASSIGNMENT.DUPLICATE_EXTERNAL_ID');
			}
		}
	};

	const onCloseModal = () => {
		onClose();
	};

	return (
		<>
			<Modal
				title={t(titleByView[currentView])}
				className="run-assigner-main"
				visible
				width={800}
				afterClose={onClose}
				onCancel={onCloseModal}
				zIndex={optimizationResult ? 1000 : 1060}
				footer={
					isReady ? (
						<UserAssignerFooter
							currentView={currentView}
							modalParams={modalParams}
							assignToRun={assignToRun}
							unassignTasks={unassignTasks}
							onCloseModal={onClose}
						/>
					) : null
				}
			>
				{!isReady && <Spinner />}
				{isReady && (
					<span>
						{currentView === 'table' && (
							<UserAssigner dateRange={dateRange} source={modalParams.source} data={modalParams.data} />
						)}
						{currentView === 'newRoute' && (
							<RunEditor
								plannedRoutes={teamPlannedRoutes}
								source={modalParams.source}
								title={t('RUN_USER_ASSIGNMENT.NEW_ROUTE_TO_DRIVER', {
									driverName: selectedDriver.name
								})}
								onRunParamsChanged={setRunParams}
								dateRange={dateRange}
							/>
						)}
						{currentView === 'optimizationView' && <OptimizationView optimizationTime={dateRange} />}
					</span>
				)}
			</Modal>
		</>
	);
};

export default observer(UserAssignerModal);
